import { inject, Injectable, OnDestroy } from '@angular/core';
import { ActionTooltipHelper, GenericActionService, UserAction } from '@mediahaven/shared';
import { ActionItemStyle, ApiFragment } from '@stylehaven/components';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export abstract class ActionService
    extends GenericActionService<ApiFragment, MediaHavenActionType>
    implements OnDestroy
{
    protected override BUTTON_STYLE: ActionItemStyle = ActionItemStyle.Primary;

    // Services
    protected readonly tooltipHelper: ActionTooltipHelper = inject(ActionTooltipHelper);

    // Observables
    // Public so the bulk action service can access it
    public readonly executingRefreshTrigger$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

    /*
    Used internally by getActionItem to build the user action.
    Used externally by the bulk action service to build the user action for each record in the bulk action.
    */
    public abstract userActionBuilder(record: ApiFragment, ...args: any[]): UserAction;

    protected override getDisabledMessage(target: ApiFragment, userAction: UserAction): string {
        return this.tooltipHelper.getActionTooltip(userAction, [target]);
    }

    // #region Executing
    public getActionExecuting(record: ApiFragment): Observable<boolean> {
        return this.executingRefreshTrigger$.pipe(
            switchMap(() =>
                this.actionsExecuting.has(record.getFragmentId())
                    ? this.actionsExecuting.get(record.getFragmentId())!
                    : of(false)
            )
        );
    }

    public setActionExecutionStart(record: ApiFragment): void {
        if (this.actionsExecuting.has(record.getFragmentId())) {
            this.actionsExecuting.get(record.getFragmentId())!.next(true);
        } else {
            this.actionsExecuting.set(record.getFragmentId(), new BehaviorSubject<boolean>(true));
        }
        this.executingRefreshTrigger$.next();
    }

    public setActionExecutionEnd(record: ApiFragment): void {
        if (this.actionsExecuting.has(record.getFragmentId())) {
            const actionStatus: BehaviorSubject<boolean> = this.actionsExecuting.get(record.getFragmentId())!;
            actionStatus.next(false);
            actionStatus.complete();
            this.actionsExecuting.delete(record.getFragmentId());
        }
    }
    // #endregion
}
